import type { Pt } from './geom';

/**
 * **牌面 IR（中间表示）。**
 *
 * 画法不直接画 Pixi，而是产出一份**只有几何和文字**的描述，
 * 由 `painter.ts` 翻译成 Pixi 对象。
 *
 * ## 为什么要多这一层
 *
 * 1. **可在 node 里断言。** 几何最容易被写错，又最难看屏幕定位
 *    （楔子角度不对、文字出框、网点漏出边）。成了普通数据就能直接算、直接测。
 * 2. **调优面板能读能写。** 面板要显示"这张牌由哪些构件组成"，
 *    数据比对象好读得多。
 * 3. **和渲染层其它地方同一个结构。** 纯的几层（`geom` / `hatch` / `ink` / `spec`）
 *    不 import Pixi，只有 `painter` 是胶水 —— 这样几何能在 node 里断言。
 *
 * ⚠️ IR 里**不出现**"半调""条纹"这样的名字 —— 那些在生成阶段就已经
 * 展开成多边形了（见 `hatch.ts`）。painter 因此可以很哑：它只认 6 种原语。
 */

export type Paint =
  | { readonly t: 'none' }
  | { readonly t: 'solid'; readonly color: number; readonly alpha: number };

export interface Stroke {
  /** 线宽（设计单位）。`line` 必填；`poly`/`circle` 可选 */
  readonly width: number;
  readonly color: number;
  readonly alpha?: number;
  readonly cap?: 'butt' | 'round';
  readonly join?: 'miter' | 'round';
  /** 虚线模式（设计单位）。⚠️ painter 会在几何层切开，不是 Pixi 的 dash */
  readonly dash?: readonly number[];
}

/**
 * 字体角色。
 *
 * ⚠️ 全项目**没有字体文件** —— "风格"靠字重对比、挖空、字距、机械斜切做出来。
 * 所以这里只分"用途"，具体的 family 由 `theme.ts` 决定。
 */
export type FontRole = 'mono' | 'display' | 'cjk';

export type Prim =
  | { readonly k: 'poly'; readonly pts: readonly Pt[]; readonly fill?: Paint; readonly stroke?: Stroke }
  | { readonly k: 'line'; readonly pts: readonly Pt[]; readonly stroke: Stroke }
  | {
      readonly k: 'circle';
      readonly x: number;
      readonly y: number;
      readonly r: number;
      readonly fill?: Paint;
      readonly stroke?: Stroke;
    }
  | {
      readonly k: 'rect';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly fill?: Paint;
      readonly stroke?: Stroke;
    }
  | {
      readonly k: 'text';
      readonly s: string;
      readonly x: number;
      readonly y: number;
      readonly size: number;
      readonly color: number;
      readonly role: FontRole;
      readonly alpha?: number;
      readonly weight?: number;
      /** 字距（设计单位） */
      readonly tracking?: number;
      readonly anchor?: 'start' | 'middle' | 'end';
      /** 每行**字符数**。给了就按字符折行（中文没有词边界，只能数字符） */
      readonly wrap?: number;
      readonly lineHeight?: number;
      /** 绕 (x,y) 旋转，角度制 */
      readonly rotate?: number;
      /**
       * 把这一行**横向缩放到正好占满这么宽**（设计单位）。
       *
       * ⚠️ 这不是"排版上的讲究"，是没有字体文件时的唯一办法。
       * 标题要两行齐宽（`ACID` / `SAMIZDAT`），而不同机器上
       * `FONT_DISPLAY` 会落到不同字体、宽度各不相同 ——
       * 所以**宽度只能量，不能猜**。给了它就由 painter 去量。
       *
       * 只缩 x 不缩 y：字形会被**横向压缩**，而"紧、扁、贴边"
       * 正好是这套标题要的样子（见 `reference-board.md` 的 Akira 一条）。
       */
      readonly fitWidth?: number;
    }
  | {
      readonly k: 'group';
      readonly prims: readonly Prim[];
      /** 组的**位置**。子图元的坐标相对它 */
      readonly at?: Pt;
      /**
       * **旋转 / 缩放的支点**，坐标是组内部的。
       *
       * ⚠️ 和 `at` 是两件事，别混：
       *
       * | 想要的效果 | 怎么设 |
       * | --- | --- |
       * | 子图元以 (0,0) 为中心，整体挪到 `P` | `at: P` |
       * | 子图元**已经是绝对坐标**，只想绕 `Q` 转一下 | `at: Q, pivot: Q` |
       *
       * 第二行那个 `at: Q, pivot: Q` 看着啰嗦，但它是 Pixi 的实际语义：
       * 变换是 `Q + R·(L − Q)`，所以位置和支点都得给。
       * 只给 `at: Q` 会把整块内容**平移到 Q 去** —— 这正是踩过的坑。
       */
      readonly pivot?: Pt;
      readonly rotate?: number;
      /** 绕支点缩放。牌面"贴歪了、还大了一圈"这种整体处理用它 */
      readonly scale?: number;
      readonly alpha?: number;
    };

/**
 * 一张牌的全部画面。
 *
 * ⚠️ 设计空间固定 `100 × 140`（见 `geom.ts`）。painter 缩放到实际 FBO 尺寸，
 * 所以这里的数字**永远**可以对着打样直接读。
 */
export interface FaceSpec {
  /** 牌面底色 */
  readonly ground: Paint;
  /**
   * **这张牌的辨识色**（页眉 / 语义色竖条 / 主图形用的那管墨）。
   *
   * UI 拿它给「被攻击的额外回合」按钮上色（见 `cardAccent`）—— 按钮
   * 因此总是和来源牌同色，扩展包换颜色不用改界面。
   */
  readonly accent: number;
  /** 底色圆角（设计单位）。0 = 直角 */
  readonly groundRadius?: number;
  /** 牌面外框 */
  readonly border?: Stroke;
  /** 内容层 */
  readonly prims: readonly Prim[];
  /**
   * 要不要上印刷层（半调/套印错位/颗粒/磨损）。
   *
   * ⚠️ `Exception` 白卡是**唯一** `false` 的 —— 它不是这台机器印的，
   * 有印刷痕迹就说不通了。见 `visual-design.md` §2。
   */
  readonly press: boolean;
  /**
   * **图形面** —— 不裁切的那一层（画在底色之上、内容之下）。
   *
   * ⚠️ 它和 `prims` 的分工是**裁不裁**，不是「在里面还是在外面」：
   *
   * | | 裁到牌内 | 谁放这里 |
   * | --- | --- | --- |
   * | `spill` | **不裁** | 图形。可以伸出牌外，也可以不伸 —— 它只是"不裁" |
   * | `prims` | 裁 | 框和字。它们长在牌上，不许出框 |
   *
   * 所以「让一条线出框」的动作是**把它挪进 `spill`**，不是改坐标。
   * 而放进 `spill` 的图形不必真的出框（`Await` 的环就没出）——
   * 它只是被允许。
   *
   * 层次：底色 → **spill** → prims → 外框。所以图形永远不会盖住字。
   */
  readonly spill?: readonly Prim[];
  /**
   * 这份 spec 的**设计空间**。省略 = `100 × 140`（一张牌）。
   *
   * ⚠️ 牌面之外的东西（牌背、标题 Logo）不一定是卡的形状，
   * 但它们必须走**同一条绘制管线** —— 否则又会分叉出第二套画法，
   * 那正是当初把 SVG 打样和 Pixi 占位并存时踩过的坑。
   * 所以让 spec **自己声明**它活在多大的画布里，painter 照它缩放。
   *
   * 调用方给的 `FaceSize` 必须和这里同比 —— painter 只按宽度算缩放系数。
   */
  readonly design?: { readonly width: number; readonly height: number };
}

// ============================================================
// 构造器 —— 让画法的代码读起来像在描述画面，而不是在拼对象
// ============================================================

export const NONE: Paint = { t: 'none' };

export const solid = (color: number, alpha = 1): Paint => ({ t: 'solid', color, alpha });

export const fillPoly = (
  pts: readonly Pt[],
  paint: Paint,
  stroke?: Stroke,
): Prim => (stroke === undefined ? { k: 'poly', pts, fill: paint } : { k: 'poly', pts, fill: paint, stroke });

export const line = (pts: readonly Pt[], stroke: Stroke): Prim => ({ k: 'line', pts, stroke });

export const dot = (x: number, y: number, r: number, paint: Paint): Prim => ({
  k: 'circle',
  x,
  y,
  r,
  fill: paint,
});

export const box = (
  x: number,
  y: number,
  w: number,
  h: number,
  paint: Paint,
  stroke?: Stroke,
): Prim => (stroke === undefined ? { k: 'rect', x, y, w, h, fill: paint } : { k: 'rect', x, y, w, h, fill: paint, stroke });

export interface TextOpts {
  readonly size: number;
  readonly color: number;
  readonly role?: FontRole;
  readonly alpha?: number;
  readonly weight?: number;
  readonly tracking?: number;
  readonly anchor?: 'start' | 'middle' | 'end';
  readonly wrap?: number;
  readonly lineHeight?: number;
  readonly rotate?: number;
  readonly fitWidth?: number;
}

export const label = (s: string, x: number, y: number, o: TextOpts): Prim => ({
  k: 'text',
  s,
  x,
  y,
  size: o.size,
  color: o.color,
  role: o.role ?? 'mono',
  ...(o.alpha === undefined ? {} : { alpha: o.alpha }),
  ...(o.weight === undefined ? {} : { weight: o.weight }),
  ...(o.tracking === undefined ? {} : { tracking: o.tracking }),
  ...(o.anchor === undefined ? {} : { anchor: o.anchor }),
  ...(o.wrap === undefined ? {} : { wrap: o.wrap }),
  ...(o.lineHeight === undefined ? {} : { lineHeight: o.lineHeight }),
  ...(o.rotate === undefined ? {} : { rotate: o.rotate }),
  ...(o.fitWidth === undefined ? {} : { fitWidth: o.fitWidth }),
});

export const group = (
  prims: readonly Prim[],
  o: { at?: Pt; pivot?: Pt; rotate?: number; scale?: number; alpha?: number } = {},
): Prim => ({
  k: 'group',
  prims,
  ...(o.at === undefined ? {} : { at: o.at }),
  ...(o.pivot === undefined ? {} : { pivot: o.pivot }),
  ...(o.rotate === undefined ? {} : { rotate: o.rotate }),
  ...(o.scale === undefined ? {} : { scale: o.scale }),
  ...(o.alpha === undefined ? {} : { alpha: o.alpha }),
});

/** 数一数这份 spec 有多少个图元 —— 调优面板和测试用它 */
export function countPrims(spec: FaceSpec): number {
  let n = 0;
  const walk = (list: readonly Prim[]): void => {
    for (const p of list) {
      n++;
      if (p.k === 'group') walk(p.prims);
    }
  };
  walk(spec.prims);
  return n;
}
